/**
 * moduleLauncher.js — data source for the PWA launcher grid
 * (pwa.hustlesuite.au).
 *
 * Merges the domain map from moduleHost.js with the 31 icon sets
 * generated by scripts/generate_module_icons.py (served from
 * /icons/<key>/…), plus a Settings tile that opens the member area.
 *
 * Each tile has:
 *   • key         — module key (matches /icons/<key>/)
 *   • title/label — display name
 *   • initials    — 1-2 char monogram (fallback if icon fails to load)
 *   • color       — brand color hex (#…)
 *   • desc        — short description
 *   • href        — absolute URL to open on click (opens app.<domain>)
 *   • icon192     — square PNG for the tile
 *   • external    — true if href is cross-origin; false for in-SPA links
 */
import { MODULES } from "@/lib/moduleHost";
import { api } from "@/lib/api";

/** Preferred tile order — user-facing modules first. */
const TILE_ORDER = [
  "eats", "shop", "plate", "directory", "stadium",
  "properties", "stays", "bnb", "diy", "travel", "roadtrip",
  "meetups", "circles", "matchmaker", "roundup",
  "tickets", "events",
  "deals", "rewards", "press", "opinions", "reviews",
  "recipes", "tackle", "campus", "chat", "mail",
  "connect", "answer", "finder", "appz",
];

/** Build launcher tiles from MODULES, deduped on `key`. */
function buildLauncherTiles() {
  const seen = new Set();
  const byKey = {};
  MODULES.forEach(([domain, m]) => {
    if (seen.has(m.key)) return;
    seen.add(m.key);
    // App URL: bare-domain → https://app.<domain><home>
    //          app.<domain> → https://<domain><home>
    const base = domain.startsWith("app.")
      ? `https://${domain}`
      : `https://app.${domain}`;
    const path = m.home && m.home !== "/" ? m.home : "";
    byKey[m.key] = {
      key: m.key,
      title: m.title,
      label: m.label,
      initials: m.initials,
      color: m.color,
      desc: m.desc,
      href: base + path,
      icon192: `/icons/${m.key}/android-icon-192.png`,
      icon512: `/icons/${m.key}/android-icon-512.png`,
      external: true,
    };
  });
  const ordered = [];
  TILE_ORDER.forEach((k) => { if (byKey[k]) { ordered.push(byKey[k]); delete byKey[k]; } });
  Object.values(byKey).forEach((tile) => ordered.push(tile));
  return ordered;
}

export const LAUNCHER_MODULES = buildLauncherTiles();

/** Settings tile — opens the member area (same-origin on pwa.hustlesuite.au). */
export const SETTINGS_TILE = {
  key: "settings",
  title: "Settings",
  label: "Settings",
  initials: "⚙",
  color: "#334155",
  desc: "Your account, profile, notifications, wallet and preferences.",
  href: "/account",
  icon192: null, // rendered as lucide <Settings /> icon instead
  external: false,
};

/** Plaza tile — separate umbrella host, hand-curated. */
export const PLAZA_TILE = {
  key: "plaza",
  title: "HustlePlaza",
  label: "Plaza",
  initials: "HP",
  color: "#8b5cf6",
  desc: "The Australian marketplace — one cart, one wallet.",
  href: "https://app.hustleplaza.au/plaza/shop",
  icon192: `/icons/manifests/../plaza/android-icon-192.png`, // may 404, uses monogram fallback
  external: true,
};

/** Full ordered list rendered on the launcher (Settings pinned first). */
export const LAUNCHER_TILES = [SETTINGS_TILE, PLAZA_TILE, ...LAUNCHER_MODULES];

/** Record a module open on the backend so Recently-Used syncs across
 *  devices. Fires-and-forgets — unauthenticated visitors silently no-op.
 *  Callers should also update localStorage for offline responsiveness. */
export async function recordServerOpen(moduleKey) {
  if (!moduleKey) return;
  try {
    await api.post("/me/recent", { module_key: moduleKey });
  } catch (_e) {
    /* silent — signed-out users still get localStorage-only recents. */
  }
}

/** Fetch server-side Recently-Used list. Returns [] for signed-out
 *  users or when the endpoint is unreachable. */
export async function fetchServerRecent(limit = 12) {
  try {
    const { data } = await api.get(`/me/recent?limit=${limit}`);
    const items = Array.isArray(data?.items) ? data.items : [];
    return items
      .filter((it) => it && it.module_key)
      .map((it) => ({
        key: it.module_key,
        ts:  typeof it.ts === "string" ? Date.parse(it.ts) : (it.ts || 0),
      }))
      .filter((it) => it.ts > 0);
  } catch (_e) {
    return [];
  }
}
